import { useEffect, useMemo, useState } from "react";
import cls from "./GameQuestionForm.module.scss";

const initial = {
  roundNumber: 1,
  answeringGender: "male",
  guessingGender: "female",
  title: "",
  description: "",
  answers: { A: "", B: "", C: "" },
  dareForAnswering: "",
  dareForGuessing: "",
};

export default function GameQuestionForm({
  initialValue,
  submitLabel,
  loading,
  onSubmit,
}) {
  const [form, setForm] = useState(initial);

  useEffect(() => {
    if (initialValue) {
      setForm({
        ...initial,
        ...initialValue,
        answers: { ...initial.answers, ...initialValue.answers },
      });
    }
  }, [initialValue]);

  const genderWarning = useMemo(
    () => form.answeringGender === form.guessingGender,
    [form.answeringGender, form.guessingGender],
  );

  const submit = (e) => {
    e.preventDefault();
    if (genderWarning) return;
    onSubmit({ ...form, roundNumber: Number(form.roundNumber) });
  };

  return (
    <form className={cls.form} onSubmit={submit}>
      <div className={cls.section}>
        <div className={cls.sectionHead}>
          <div>
            <h3 className={cls.sectionTitle}>Question Setup</h3>
            <p className={cls.sectionSub}>
              Set the round, answer flow, and core question details.
            </p>
          </div>
        </div>

        <div className={cls.grid}>
          <div className={cls.field}>
            <label className={cls.label}>Round Number</label>
            <input
              className={cls.input}
              type="number"
              min="1"
              max="50"
              value={form.roundNumber}
              onChange={(e) =>
                setForm((p) => ({ ...p, roundNumber: e.target.value }))
              }
            />
          </div>

          <div className={cls.field}>
            <label className={cls.label}>Answering Gender</label>
            <select
              className={cls.input}
              value={form.answeringGender}
              onChange={(e) =>
                setForm((p) => ({ ...p, answeringGender: e.target.value }))
              }
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          <div className={cls.field}>
            <label className={cls.label}>Guessing Gender</label>
            <select
              className={cls.input}
              value={form.guessingGender}
              onChange={(e) =>
                setForm((p) => ({ ...p, guessingGender: e.target.value }))
              }
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          <div className={cls.fieldWide}>
            <label className={cls.label}>Question Title</label>
            <input
              className={cls.input}
              placeholder="Enter question title"
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              required
            />
          </div>

          <div className={cls.fieldWide}>
            <label className={cls.label}>Description</label>
            <textarea
              className={cls.textarea}
              placeholder="Add a supporting description if needed"
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
            />
          </div>
        </div>
      </div>

      <div className={cls.section}>
        <div className={cls.sectionHead}>
          <div>
            <h3 className={cls.sectionTitle}>Answer Choices</h3>
            <p className={cls.sectionSub}>
              Add the three selectable answer options for this question.
            </p>
          </div>
        </div>

        <div className={cls.answerGrid}>
          {["A", "B", "C"].map((k, index) => (
            <div className={cls.answerCard} key={k}>
              <div className={cls.answerTop}>
                <span className={cls.answerBadge}>Choice {index + 1}</span>
                <span className={cls.answerKey}>{k}</span>
              </div>

              <div className={cls.field}>
                <label className={cls.label}>Answer {k}</label>
                <textarea
                  className={cls.textareaMd}
                  placeholder={`Enter answer ${k}`}
                  value={form.answers[k]}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      answers: { ...p.answers, [k]: e.target.value },
                    }))
                  }
                  required
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={cls.section}>
        <div className={cls.sectionHead}>
          <div>
            <h3 className={cls.sectionTitle}>Dares</h3>
            <p className={cls.sectionSub}>
              Add optional dare text for the answering and guessing players.
            </p>
          </div>
        </div>

        <div className={cls.gridTwo}>
          <div className={cls.field}>
            <label className={cls.label}>Dare for Answering</label>
            <textarea
              className={cls.textareaLg}
              placeholder="Enter dare for answering player"
              value={form.dareForAnswering}
              onChange={(e) =>
                setForm((p) => ({ ...p, dareForAnswering: e.target.value }))
              }
            />
          </div>

          <div className={cls.field}>
            <label className={cls.label}>Dare for Guessing</label>
            <textarea
              className={cls.textareaLg}
              placeholder="Enter dare for guessing player"
              value={form.dareForGuessing}
              onChange={(e) =>
                setForm((p) => ({ ...p, dareForGuessing: e.target.value }))
              }
            />
          </div>
        </div>
      </div>

      {genderWarning ? (
        <div className={cls.errorBox}>
          <span className={cls.errorIcon}>!</span>
          <p className={cls.error}>
            Answering and guessing genders must be opposite.
          </p>
        </div>
      ) : null}

      <div className={cls.actions}>
        <button className={cls.btn} disabled={loading} type="submit">
          <span className={cls.btnGlow} />
          <span>{loading ? "Saving..." : submitLabel}</span>
        </button>
      </div>
    </form>
  );
}
